// PO-5: Standard Rovasys Allied Works Pvt. Ltd. Purchase Order Terms & Conditions.
// Source: client-supplied "PURCHASE ORDER TERMS & CONDITIONS.pdf" (30 clauses).
// Rendered as an annexure on the PO print (POShow). The per-PO free-text
// `terms_conditions` field is shown separately as PO-specific terms.
//
// PO-6 (separate T&C per category — Solar / Fabrication / Service) still needs
// the category-specific term text from the client; this constant is the general
// set that applies to every PO.

export interface POTermsSection {
  title: string;
  /** Plain paragraphs. */
  paras?: string[];
  /** Bullet lists keyed after an optional lead-in paragraph. */
  bullets?: string[];
}

export const ROVASYS_PO_TERMS: POTermsSection[] = [
  {
    title: "1. Scope and Applicability",
    paras: [
      "These General Purchase Order Terms and Conditions (\"Terms\") shall govern all Purchase Orders (\"POs\") issued by Rovasys Allied Works Pvt. Ltd. (\"Buyer\") to any Vendor, Supplier, Contractor, Consultant, or Service Provider (\"Seller\") for the supply of goods, materials, fabricated components, equipment, consumables, or services.",
      "Acceptance of a Purchase Order, commencement of work, dispatch of materials, issuance of invoice, or performance of any obligation under the PO shall constitute unconditional acceptance of these Terms.",
    ],
  },
  {
    title: "2. Purchase Order Acceptance",
    paras: [
      "2.1 The Seller shall acknowledge acceptance of the Purchase Order within two (2) working days from the date of receipt.",
      "2.2 Failure to communicate objections in writing within the stipulated period shall be deemed acceptance of the Purchase Order in its entirety.",
      "2.3 Any amendment, deviation, or variation from the Purchase Order shall be valid only if approved by the Buyer in writing.",
      "2.4 Verbal instructions or commitments shall not modify the Purchase Order.",
    ],
  },
  {
    title: "3. Scope of Supply",
    paras: ["3.1 The Seller shall supply goods and/or services strictly in accordance with:"],
    bullets: [
      "Approved drawings,",
      "Technical specifications,",
      "Bill of Quantities (BOQ),",
      "Applicable standards,",
      "Quality plans,",
      "Inspection requirements, and",
      "Purchase Order requirements.",
    ],
  },
  {
    title: "4. Quality Assurance and Inspection",
    paras: [
      "4.1 The Buyer reserves the right to inspect, audit, witness tests, and verify manufacturing processes at the Seller's premises at any stage.",
      "4.2 Inspection by the Buyer shall not relieve the Seller of responsibility for quality.",
      "4.3 The Seller shall provide all required quality documents, including Mill/Material Test Certificates, CMM Reports, Coating & Salt Spray Reports, Inspection & Dimensional Reports, Calibration Certificates, QAP, and any other documents specified in the PO.",
      "4.4 Materials failing inspection may be rejected without prejudice to other remedies available to the Buyer.",
    ],
  },
  {
    title: "5. Prototype, Sample Approval and Manufacturing Clearance",
    paras: [
      "5.1 Wherever applicable, prototype samples shall be submitted free of cost.",
      "5.2 Bulk production shall commence only after obtaining written approval from the Buyer.",
      "5.3 Seller shall obtain all required approvals including QAP, MDCC, customer approvals, or third-party inspection clearances prior to mass production.",
    ],
  },
  {
    title: "6. Delivery Obligations",
    paras: [
      "6.1 Delivery schedules specified in the Purchase Order shall be strictly adhered to.",
      "6.2 The Seller shall immediately notify the Buyer of any anticipated delay.",
      "6.3 Partial deliveries shall require prior written approval.",
      "6.4 Time shall be of the essence of the contract.",
    ],
  },
  {
    title: "7. Packaging, Marking and Transportation",
    paras: [
      "7.1 Seller shall ensure proper packing suitable for transportation, handling, storage, and climatic conditions.",
      "7.2 All packages shall clearly indicate PO Number, Item Description, Quantity, Weight, Consignee Details, and Handling Instructions.",
      "7.3 Transportation arrangements shall be as specified in the Purchase Order.",
      "7.4 Unless otherwise agreed, risk shall remain with the Seller until acceptance by the Buyer.",
    ],
  },
  {
    title: "8. Dispatch Documentation",
    paras: ["Each dispatch shall be accompanied by applicable documents, including Tax Invoice, Delivery Challan, E-Way Bill, Packing List, Test Certificates, Inspection Reports, Warranty Certificates, Weight Slips, Compliance Certificates, and any other documents specified by the Buyer. Failure to provide complete documentation may result in rejection or delay in payment processing."],
  },
  {
    title: "9. Inspection, Rejection and Replacement",
    paras: [
      "9.1 Buyer may reject goods that are defective, damaged, non-conforming, counterfeit, expired, or deviating from approved specifications.",
      "9.2 Seller shall replace rejected materials immediately at its own cost.",
      "9.3 Transportation, handling, replacement, and associated expenses shall be borne by the Seller.",
      "9.4 Repeated quality failures may result in suspension or blacklisting of the Seller.",
    ],
  },
  {
    title: "10. Price Basis",
    paras: [
      "10.1 Prices shall remain firm and fixed during the validity of the Purchase Order unless otherwise agreed in writing.",
      "10.2 Prices shall include all costs specified in the Purchase Order.",
      "10.3 No escalation shall be accepted without prior written approval.",
    ],
  },
  {
    title: "11. Taxes and Statutory Compliance",
    paras: [
      "11.1 Seller shall comply with all applicable laws, regulations, and statutory requirements.",
      "11.2 GST shall be charged as per prevailing laws.",
      "11.3 Seller shall timely file GSTR-1 and GSTR-3B, generate valid IRN and QR Code wherever applicable, and ensure tax compliance enabling Buyer to avail Input Tax Credit.",
      "11.4 Seller shall indemnify Buyer against losses arising from tax non-compliance.",
    ],
  },
  {
    title: "12. TDS, TCS and MSME Compliance",
    paras: [
      "12.1 Buyer shall deduct taxes at source as per applicable laws.",
      "12.2 Seller shall comply with provisions relating to TCS wherever applicable.",
      "12.3 MSME vendors shall furnish valid registration certificates.",
      "12.4 Any statutory benefits claimed shall be supported by documentary evidence.",
    ],
  },
  {
    title: "13. Payment Terms",
    paras: [
      "13.1 Payment terms shall be as specified in the Purchase Order.",
      "13.2 Payments shall be processed only upon receipt of goods, acceptance after inspection, submission of complete documentation, and compliance with statutory requirements.",
      "13.3 Buyer reserves the right to withhold disputed amounts.",
      "13.4 Acceptance of goods shall not prejudice Buyer's right to reject latent defects discovered subsequently.",
    ],
  },
  {
    title: "14. Warranty",
    paras: [
      "14.1 Seller warrants that goods supplied are new, free from defects, of merchantable quality, and fit for intended purpose.",
      "14.2 Unless otherwise specified, warranty shall be twelve (12) months from acceptance or eighteen (18) months from dispatch, whichever occurs earlier.",
      "14.3 Defective goods shall be repaired or replaced by the Seller at no additional cost.",
    ],
  },
  {
    title: "15. Liquidated Damages",
    paras: [
      "15.1 Delay in delivery shall attract Liquidated Damages at the rate of 1% of the undelivered Purchase Order value per week of delay.",
      "15.2 Maximum Liquidated Damages shall not exceed 10% of the Purchase Order value.",
      "15.3 Buyer reserves the right to recover additional losses where legally permissible.",
    ],
  },
  {
    title: "16. Alternate Procurement",
    paras: ["If the Seller fails to meet obligations relating to quality, delivery, or performance, Buyer may procure materials or services from alternate sources. Any excess costs incurred shall be recoverable from the Seller."],
  },
  {
    title: "17. Ethics and Anti-Bribery",
    paras: [
      "17.1 Seller shall maintain the highest standards of integrity.",
      "17.2 Seller shall not offer gifts, hospitality, commissions, kickbacks, or inducements to Buyer's employees or representatives.",
      "17.3 Any violation shall constitute a material breach of contract.",
    ],
  },
  {
    title: "18. Environment, Health and Safety (EHS)",
    paras: ["Seller shall comply with environmental laws, provide safe working conditions, prohibit child and forced labour, follow occupational health and safety requirements, and ensure proper waste disposal practices."],
  },
  {
    title: "19. Confidentiality",
    paras: [
      "19.1 All commercial and technical information shared by Buyer shall remain confidential.",
      "19.2 Seller shall not disclose such information without Buyer's written consent.",
      "19.3 Confidentiality obligations shall survive termination of the Purchase Order.",
    ],
  },
  {
    title: "20. Intellectual Property",
    paras: [
      "20.1 Drawings, designs, specifications, tooling, samples, and technical information supplied by Buyer shall remain Buyer's property.",
      "20.2 Seller shall not manufacture, sell, reproduce, or disclose such proprietary information to third parties.",
      "20.3 All Buyer-owned assets shall be returned upon demand.",
    ],
  },
  {
    title: "21. Insurance",
    paras: ["Seller shall maintain adequate insurance coverage for goods in transit, workmen compensation, public liability, and any other insurance required by law."],
  },
  {
    title: "22. Indemnity",
    paras: ["Seller shall indemnify and hold harmless Buyer against claims arising from defective products, personal injury, property damage, intellectual property infringement, statutory violations, third-party claims, and tax liabilities attributable to Seller's non-compliance."],
  },
  {
    title: "23. Force Majeure",
    paras: ["Neither party shall be liable for failure to perform obligations due to events beyond reasonable control including natural disasters, war, riots, strikes, epidemics, or governmental restrictions. The affected party shall notify the other party within seven (7) days of occurrence."],
  },
  {
    title: "24. Suspension and Termination",
    paras: ["Buyer may suspend or terminate the Purchase Order without liability in the event of persistent delays, quality failures, breach of contract, ethical violations, insolvency, or statutory non-compliance. Termination shall be without prejudice to Buyer's other legal remedies."],
  },
  {
    title: "25. Vendor Performance Evaluation",
    paras: ["Buyer reserves the right to periodically assess Seller performance based on quality, delivery adherence, responsiveness, documentation compliance, commercial conduct, and corrective action closure. Repeated unsatisfactory performance may lead to suspension or blacklisting."],
  },
  {
    title: "26. Assignment and Subcontracting",
    paras: ["Seller shall not assign, transfer, or subcontract any part of the Purchase Order without prior written approval from Buyer."],
  },
  {
    title: "27. Notices",
    paras: ["All notices relating to the Purchase Order shall be made in writing and sent through authorised communication channels."],
  },
  {
    title: "28. Dispute Resolution and Jurisdiction",
    paras: ["The parties shall endeavour to resolve disputes amicably through mutual discussions. In the event of failure to resolve, disputes shall be subject to the exclusive jurisdiction of the courts at Pune, Maharashtra, India."],
  },
  {
    title: "29. Severability",
    paras: ["If any provision of these Terms is held invalid or unenforceable, the remaining provisions shall remain valid and enforceable."],
  },
  {
    title: "30. Entire Agreement",
    paras: ["These Terms and Conditions together with the Purchase Order constitute the complete agreement between the parties and supersede all prior communications relating to the subject matter. No modification shall be valid unless approved in writing by authorised representatives of the Buyer."],
  },
];
